/* Public teams: /teams = card grid (logo, name, roster avatars, avg FACEIT ELO; search + sort),
 * /teams/:id = team page (hero, roster cards with FACEIT level, recent matches with W/L).
 * Data: GET /api/teams/public/list (archived teams hidden, rosters included) and /api/teams/public/:id. */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicHeader from './PublicHeader';
import Footer from './Footer';
import usePageTitle from '../utils/usePageTitle';
import { publicApi, TeamLogo, PlayerAvatar, FaceitElo, prettyMap, fmtDay } from './public/pubUi';
import './public/publicPages.css';

/* ── TEAMS LIST ─────────────────────────────────────────── */
function TeamCard({ tm }) {
  const { t } = useTranslation();
  const players = [...(tm.players || [])].sort((a, b) => (a.role === 'sub') - (b.role === 'sub'));
  const shown = players.slice(0, 5);
  const more = players.length - shown.length;
  return (
    <Link to={`/teams/${tm.id}`} className="pp-tcard">
      <span className="pp-tcard-top">
        <TeamLogo id={tm.id} name={tm.name} avatarV={tm.avatar_v} logoUrl={tm.logo_url} size={56} />
        <span className="pp-tcard-title">
          <b title={tm.name}>{tm.name}</b>
          {tm.tag && <span className="pp-tag">{tm.tag}</span>}
        </span>
      </span>
      <span className="pp-tcard-roster">
        {shown.length ? (
          <span className="pp-stack">
            {shown.map(p => <PlayerAvatar key={p.steam_id} steamId={p.steam_id} avatarV={p.avatar_v} name={p.name} size={28} />)}
            {more > 0 && <span className="pp-stack-more">+{more}</span>}
          </span>
        ) : <span className="pp-muted">{t('publicTeams.noPlayers', 'No players yet')}</span>}
        <span className="pp-muted">{t('publicTeams.playersN', 'Players: {{count}}', { count: players.length })}</span>
      </span>
      <span className="pp-tcard-foot">
        {tm.avg_faceit_elo != null
          ? <FaceitElo elo={tm.avg_faceit_elo} label={t('publicTeams.avgElo', 'Avg ELO')} />
          : <span className="pp-muted">{t('publicTeams.noElo', 'No FACEIT data')}</span>}
        <span className="pp-tcard-open">{t('publicTeams.view', 'View')} ›</span>
      </span>
    </Link>
  );
}

function TeamsList() {
  const { t } = useTranslation();
  usePageTitle(t('seo.teamsTitle', 'Teams'));
  const [teams, setTeams] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name');

  useEffect(() => {
    let alive = true;
    publicApi.get('/api/teams/public/list', { params: { limit: 500 } })
      .then(r => { if (alive) setTeams(r.data?.data ?? []); })
      .catch(() => { if (alive) setTeams([]); });
    return () => { alive = false; };
  }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (teams || []).filter(x => !q || (x.name || '').toLowerCase().includes(q) || (x.tag || '').toLowerCase().includes(q)
      || (x.players || []).some(p => (p.name || '').toLowerCase().includes(q)));
    const by = {
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      elo: (a, b) => (b.avg_faceit_elo ?? -1) - (a.avg_faceit_elo ?? -1) || (a.name || '').localeCompare(b.name || ''),
      players: (a, b) => (b.players || []).length - (a.players || []).length || (a.name || '').localeCompare(b.name || ''),
    }[sort];
    return [...list].sort(by);
  }, [teams, search, sort]);

  return (
    <div className="pub-root pp-root">
      <PublicHeader />
      <div className="pp-wrap">
        <div className="pp-head">
          <div>
            <h1>{t('publicTeams.title', 'Teams')}</h1>
            <p>{t('publicTeams.subtitle', 'All registered teams and their rosters.')}</p>
          </div>
          {teams && <span className="pp-head-count">{t('publicTeams.teamsN', 'Teams: {{count}}', { count: teams.length })}</span>}
        </div>

        <div className="pp-toolbar">
          <label className="pp-search wide">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('publicTeams.searchPh', 'Search team, tag or player…')} />
            {search && <button type="button" onClick={() => setSearch('')} aria-label={t('common.clear', 'Clear')}>×</button>}
          </label>
          <div className="pp-tabs" role="tablist" aria-label={t('publicTeams.sortBy', 'Sort by')}>
            {[['name', t('publicTeams.sortName', 'A–Z')], ['elo', t('publicTeams.sortElo', 'ELO')], ['players', t('publicTeams.sortPlayers', 'Players')]].map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={sort === k} className={`pp-tab${sort === k ? ' on' : ''}`} onClick={() => setSort(k)}>{label}</button>
            ))}
          </div>
        </div>

        {teams === null ? (
          <div className="pp-tgrid">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="pp-tcard skel" />)}</div>
        ) : shown.length === 0 ? (
          <div className="pp-empty">{search.trim() ? t('publicTeams.noMatch', 'No teams match “{{q}}”.', { q: search.trim() }) : t('publicTeams.noTeams', 'No teams registered yet.')}</div>
        ) : (
          <div className="pp-tgrid">{shown.map(tm => <TeamCard key={tm.id} tm={tm} />)}</div>
        )}
      </div>
      <Footer />
    </div>
  );
}

/* ── TEAM PAGE ──────────────────────────────────────────── */
function TeamPage({ id }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [team, setTeam] = useState(undefined); // undefined = loading, null = not found
  usePageTitle(team ? team.name : null);

  useEffect(() => {
    let alive = true;
    setTeam(undefined);
    publicApi.get(`/api/teams/public/${id}`)
      .then(r => { if (alive) setTeam(r.data); })
      .catch(() => { if (alive) setTeam(null); });
    return () => { alive = false; };
  }, [id]);

  const recent = useMemo(() => (team?.recent_matches || []).map(m => {
    const isT1 = Number(m.team1_id) === Number(team.id);
    const bo = parseInt(m.best_of, 10) || 1;
    const mine = bo > 1 ? (isT1 ? m.map_wins_team1 : m.map_wins_team2) : (isT1 ? m.score_team1 : m.score_team2);
    const opp = bo > 1 ? (isT1 ? m.map_wins_team2 : m.map_wins_team1) : (isT1 ? m.score_team2 : m.score_team1);
    const done = m.status === 'finished';
    return {
      ...m, bo, mine: mine ?? 0, opp: opp ?? 0,
      result: m.status === 'live' ? 'live' : !done ? 'up' : (mine ?? 0) > (opp ?? 0) ? 'w' : (mine ?? 0) < (opp ?? 0) ? 'l' : 'd',
      oppId: isT1 ? m.team2_id : m.team1_id, oppName: isT1 ? m.team2_name : m.team1_name, oppV: isT1 ? m.team2_avatar_v : m.team1_avatar_v,
    };
  }), [team]);
  const record = recent.reduce((r, m) => { if (m.result === 'w') r.w++; else if (m.result === 'l') r.l++; return r; }, { w: 0, l: 0 });

  if (team === undefined) {
    return <div className="pub-root pp-root"><PublicHeader /><div className="pp-wrap"><div className="pp-hero skel" /></div><Footer /></div>;
  }
  if (team === null) {
    return (
      <div className="pub-root pp-root"><PublicHeader />
        <div className="pp-wrap"><div className="pp-empty">{t('publicTeams.notFound', 'Team not found.')} <Link to="/teams">{t('publicTeams.backToTeams', 'All teams')}</Link></div></div>
        <Footer />
      </div>
    );
  }

  const players = [...(team.players || [])].sort((a, b) => (a.role === 'sub') - (b.role === 'sub'));
  const resultLabel = { w: t('publicTeams.win', 'W'), l: t('publicTeams.loss', 'L'), d: t('publicTeams.draw', 'D'), live: t('publicMatches.live', 'Live'), up: t('publicTeams.upcomingShort', 'Next') };

  return (
    <div className="pub-root pp-root">
      <PublicHeader />
      <div className="pp-wrap">
        <button type="button" className="pp-back" onClick={() => navigate('/teams')}>← {t('publicTeams.backToTeams', 'All teams')}</button>

        <section className="pp-hero">
          <TeamLogo id={team.id} name={team.name} avatarV={team.avatar_v} logoUrl={team.logo_url} size={88} />
          <div className="pp-hero-text">
            <h1>{team.name}{team.tag && <span className="pp-tag big">{team.tag}</span>}</h1>
            <div className="pp-hero-stats">
              <span><b>{players.length}</b> {t('publicTeams.players', 'Players')}</span>
              {team.coach_name && <span>{t('publicTeams.coach', 'Coach')} <b>{team.coach_name}</b></span>}
              {(record.w + record.l) > 0 && <span>{t('publicTeams.recent', 'Recent')} <b className="w">{record.w}W</b> <b className="l">{record.l}L</b></span>}
              {team.avg_faceit_elo != null && <FaceitElo elo={team.avg_faceit_elo} label={t('publicTeams.avgElo', 'Avg ELO')} />}
            </div>
          </div>
        </section>

        <section className="pp-section">
          <h2>{t('publicTeams.roster', 'Roster')}</h2>
          {players.length === 0 ? <div className="pp-empty">{t('publicTeams.noPlayersRoster', 'No players on this roster yet.')}</div> : (
            <div className="pp-roster">
              {players.map(p => {
                const steamUrl = p.steam_id ? `https://steamcommunity.com/profiles/${p.steam_id}` : null;
                const Tag = steamUrl ? 'a' : 'div';
                return (
                  <Tag key={p.id || p.steam_id} className="pp-player" {...(steamUrl ? { href: steamUrl, target: '_blank', rel: 'noopener noreferrer', title: t('publicTeams.steamProfile', 'Steam profile') } : {})}>
                    <PlayerAvatar steamId={p.steam_id} avatarV={p.avatar_v} name={p.name} size={48} />
                    <span className="pp-player-text">
                      <b title={p.name}>{p.name || p.steam_id}</b>
                      <span className="pp-player-sub">
                        {p.role === 'sub' && <span className="pp-chip">{t('publicTeams.sub', 'Sub')}</span>}
                        {steamUrl && <span className="pp-muted">Steam ↗</span>}
                      </span>
                    </span>
                    {p.faceit && p.faceit.faceit_elo != null && <FaceitElo elo={Math.round(p.faceit.faceit_elo)} level={p.faceit.skill_level} />}
                  </Tag>
                );
              })}
            </div>
          )}
        </section>

        {recent.length > 0 && (
          <section className="pp-section">
            <h2>{t('publicTeams.recentMatches', 'Recent matches')}</h2>
            <div className="pp-list">
              {recent.map(m => (
                <Link key={m.id} to={`/match/${m.id}`} className="pp-trow">
                  <span className={`pp-res ${m.result}`}>{resultLabel[m.result]}</span>
                  <span className="pp-trow-opp"><span className="pp-muted">{t('publicMatches.vs', 'vs')}</span><TeamLogo id={m.oppId} name={m.oppName} avatarV={m.oppV} size={24} /><b title={m.oppName}>{m.oppName}</b></span>
                  <span className="pp-row-score">
                    {m.result === 'up' ? <span className="pp-vs">–</span> : <><b className={m.result === 'w' ? 'win' : ''}>{m.mine}</b><i>:</i><b className={m.result === 'l' ? 'win' : ''}>{m.opp}</b></>}
                  </span>
                  <span className="pp-row-format">{m.bo > 1 ? `BO${m.bo}` : (prettyMap(m.map) || t('publicMatches.tbd', 'TBD'))}</span>
                  <span className="pp-row-date"><b>{fmtDay(m.end_time || m.created_at, i18n.language)}</b></span>
                  <span className="pp-row-arrow" aria-hidden="true">›</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
}

function PublicTeams() {
  const { id } = useParams();
  return id ? <TeamPage id={id} /> : <TeamsList />;
}

export default PublicTeams;
